import { css } from '@emotion/core'
import api from 'api'
import { Button, Cell, DataTable, Grid, HFlow, Icon, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { TitleGroup } from 'components/TitleGroup'
import { useUltimosCincoArquivosTemporariosQuery } from 'graphql/hooks.generated'
import {
  ArquivoTemporario,
  ArquivoTemporarioIdentificadorEnum,
  ArquivoTemporarioStatusEnum,
} from 'graphql/types.generated'
import { useDownloadFile } from 'hooks/impressao/useDownloadFile'
import React, { useState } from 'react'
import { formatDateAndHoursMinutes } from 'util/date/formatDate'
import { ArquivoTemporarioModel, relatoriosRecentesRecord } from 'view/arquivo/model-arquivo'
import StatusArquivoTemporarioLabel from 'view/arquivo/StatusArquivoTemporarioLabel'

import { ImportacaoCidadaoListener } from './ImportacaoCidadaoListener'

export const IMPORTAR_CIDADAO_PATH = 'importar-cidadao'

export function UltimasImportacoesCidadaoTable({ municipio }) {
  const { fileName } = relatoriosRecentesRecord[ArquivoTemporarioIdentificadorEnum.RELATORIO_IMPORTACAO_CIDADAOS]

  const { data, loading, refetch } = useUltimosCincoArquivosTemporariosQuery({
    variables: {
      input: ArquivoTemporarioIdentificadorEnum.RELATORIO_IMPORTACAO_CIDADAOS,
    },
  })

  const [mostrarMais, setMostrarMais] = useState<boolean>(false)

  const theme = useTheme()
  const styles = createStyles(theme)
  const { downloadFileWithPath } = useDownloadFile()

  const handleDownload = (item: ArquivoTemporario) => {
    if (item.status === ArquivoTemporarioStatusEnum.PRONTO)
      downloadFileWithPath({
        apiPath: api.arquivoTemporario.downloadArquivo,
        values: item.id,
        file: `${fileName}.${item.formato.toLocaleLowerCase()}`,
      })
  }

  const ultimosCincoArquivos = !loading ? data?.ultimosCincoArquivosTemporarios : []
  const first = ultimosCincoArquivos.isNotEmpty() ? [ultimosCincoArquivos[0]] : []

  return (
    <>
      <ImportacaoCidadaoListener onImportacaoMessage={refetch} municipio={municipio.id} />
      <TitleGroup title='Arquivos importados (7 dias)' />
      <Grid>
        <Cell size={12} style={styles.cell} alignSelf='flex-start'>
          <VFlow vSpacing={0} style={styles.table}>
            <DataTable<ArquivoTemporarioModel>
              rows={mostrarMais ? ultimosCincoArquivos : first}
              columns={[
                {
                  name: 'dataRequisicao',
                  header: 'Data da importação',
                  render: (item) => formatDateAndHoursMinutes(item.dataInicioGerado),
                },
                {
                  name: 'statusProcessamento',
                  header: 'Status da importação',
                  render: (item) => (
                    <StatusArquivoTemporarioLabel
                      arquivoTemporarioStatus={item.status}
                      dataInicioGerado={item.dataInicioGerado}
                      isApenasUltimoRelatorio={false}
                    />
                  ),
                },
                {
                  name: 'download',
                  header: '',
                  render: (item) => {
                    return (
                      <Button
                        type='button'
                        skin='ghost'
                        size='small'
                        disabled={item.status !== ArquivoTemporarioStatusEnum.PRONTO}
                        onClick={() => handleDownload(item)}
                      >
                        <Tooltip text={item.status === ArquivoTemporarioStatusEnum.PRONTO ? 'Download' : ''}>
                          <Icon icon='download' />
                        </Tooltip>
                      </Button>
                    )
                  },
                },
              ]}
            />
            <HFlow style={styles.footer}>
              <Button
                type='button'
                skin='ghost'
                size='small'
                onClick={() => {
                  setMostrarMais(!mostrarMais)
                }}
                style={styles.mostrarMais}
              >
                <Icon icon={mostrarMais ? 'angleUp' : 'angleDown'} />
                <Text>{mostrarMais ? 'Mostrar menos' : 'Mostrar mais'}</Text>
              </Button>
            </HFlow>
          </VFlow>
        </Cell>
      </Grid>
    </>
  )
}
const createStyles = (theme: Theme) => ({
  cell: css`
    padding-top: 2rem;
    padding-left: 0rem;
  `,
  footer: css`
    display: flex;
    align-items: stretch;
    border-left: 1px solid ${theme.pallete.divider};
    border-right: 1px solid ${theme.pallete.divider};
    border-bottom: 1px solid ${theme.pallete.divider};
    padding-top: 0rem;
    padding-left: 1rem;
    height: 2.25rem;
  `,
  mostrarMais: css`
    padding-top: 0.35rem;
  `,
  table: css`
    background: white;
  `,
})
